import React, { createContext, useContext, useEffect, useState } from 'react'
import { useTranslation } from '@mc/i18n'
import { UserAnalytics } from '@mc/jarvis-api'
import { useGlobalErrorModal } from '../components/GlobalErrorModal'
import { forceLogout } from '../utils/logout'

export interface JarvisUser {
  sub: string
  name?: string
  email?: string
  picture?: string
  nickname?: string
  updated_at?: string
  analytics?: UserAnalytics
}

interface AuthContextValue {
  user: JarvisUser | undefined
  error: Error | undefined
  isLoading: boolean
}

const AuthContext = createContext<AuthContextValue>({
  user: undefined,
  error: undefined,
  isLoading: true,
})

export const useUser = () => useContext(AuthContext)

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation('@mc/jarvis')
  const { openModal } = useGlobalErrorModal()
  const [user, setUser] = useState<JarvisUser | undefined>(undefined)
  const [error, setError] = useState<Error | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)

  const shouldRedirectToLogin = () =>
    typeof window !== 'undefined' &&
    !window.location.pathname.startsWith('/login') &&
    !window.location.pathname.startsWith('/welcome')

  useEffect(() => {
    const fetchUser = async () => {
      const showInvalidAccountModal = () => {
        openModal({
          title: t('globalError.invalidAccountTitle'),
          description: t('globalError.invalidAccountDescription'),
          primaryActionLabel: t('globalError.invalidAccountCta'),
          onPrimaryAction: () => {
            forceLogout()
          },
        })
      }

      try {
        const response = await fetch('/api/jarvis/auth/me')

        if (response.ok) {
          const userData = await response.json()
          setUser(userData)
        } else if (response.status === 403) {
          setUser(undefined)
          showInvalidAccountModal()
        } else {
          setUser(undefined)
          if (shouldRedirectToLogin()) {
            await forceLogout()
          }
        }
      } catch (err) {
        setError(err as Error)
        setUser(undefined)
        if (shouldRedirectToLogin()) {
          await forceLogout()
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchUser()
  }, [openModal, t])

  return (
    <AuthContext.Provider value={{ user, error, isLoading }}>
      {children}
    </AuthContext.Provider>
  )
}
